import React from 'react'
import { useParams } from 'react-router-dom'
import CollectionService from '../../service/CollectionService'
import Collection, { CollectionRetrieve } from '../../domain/Collection'
import { statusLabel } from '../../domain/Status'
import useEntity from '../../hooks/useEntity'
import { paths } from '../../routes'
import FindBox from '../ui/FindBox'
import EntityState from '../ui/EntityState'
import EntitySummary from '../ui/EntitySummary'
import StatusChanger from '../ui/StatusChanger'

const providerLabels: Record<string, string> = {
    dbs_sg: 'DBS Singapore',
    dbs_hk: 'DBS Hong Kong',
}

const CollectionAccountUpdateForm: React.FC = () => {
    const { accountNumber } = useParams()
    const {
        entity: account,
        setEntity,
        loading,
        error,
        find,
        refresh,
    } = useEntity<CollectionRetrieve>({
        kind: 'collectionAccount',
        id: accountNumber,
        retrieve: (vaNumber) => CollectionService.retrieveCollectionAccount(vaNumber),
        pathFor: paths.collection.account,
        notFound: 'Collection account not found. Check the VA number and try again.',
        remember: (found) => ({ label: found.name, accountId: found.accountId, accountNumber: found.accountNumber }),
    })

    const changeStatus = (status: string) => {
        const { accountNumber: vaNumber, provider } = account as CollectionRetrieve
        return CollectionService.updateCollectionAccountStatus({ accountNumber: vaNumber, status, provider }).then(() =>
            setEntity((current) => current && { ...current, status })
        )
    }

    return (
        <div className="stack">
            <FindBox kind="collectionAccount" title="Find a Collection Account" placeholder="Enter VA Number" value={accountNumber} loading={loading} onFind={find} />
            <EntityState id={accountNumber} loading={loading} error={error} ready={!!account} emptyText="Enter a VA number above, or pick a recent one.">
                {account && (
                    <>
                        <EntitySummary
                            title="Collection Account Information"
                            status={account.status}
                            statusLabel={statusLabel(account.status, Collection.accountStatuses(account))}
                            items={[
                                { label: 'Account name', value: account.name },
                                { label: 'VA number', copy: account.accountNumber },
                                { label: 'Provider', value: account.provider && (providerLabels[account.provider] ?? account.provider) },
                                { label: 'Account ID', copy: account.accountId },
                            ]}
                            refreshing={loading}
                            onRefresh={refresh}
                        />
                        <StatusChanger
                            noun="collection account"
                            current={account.status}
                            statuses={Collection.accountStatuses(account)}
                            journey={Collection.accountJourney(account.provider)}
                            hints={Collection.accountHints}
                            onSubmit={changeStatus}
                        />
                    </>
                )}
            </EntityState>
        </div>
    )
}

export default CollectionAccountUpdateForm
